import { observer } from 'mobx-react';
import { makeAutoObservable } from 'mobx';
import { IUserConfigPackage } from '../../../API';
import { IAIPropertyDefinition, IEntityColumn, IEntityToken } from '../../../API';
import ReusableDialog, { IReusableDialogFooterProps, ReusableDialogState } from '../../controls/ReusableDialog';
import { InfoTooltipLabel } from '../../controls/InfoTooltipLabel';
import {  Dropdown, IDropdownOption, MessageBar, TextField } from '@fluentui/react';
import { useEffect } from 'react';
import ExpressionField, { ExpressionFieldState } from '../expression-builder/ExpressionField';
import { ExpressionBuilderOutputType } from '../expression-builder/ExpressionBuilder';

export class AIPropertyEditorState implements IAIPropertyDefinition {
	
	public userConfig: IUserConfigPackage;

	public dialog:ReusableDialogState;
	public expressionState:ExpressionFieldState;

	public Id: string = '';

	public FieldName: string = '';
	public Prompt: string = '';
	public WorkspacePath: string = '';
	public Expression: string = '';

	//Unused
	//-------------
	public DisplayTitle: string = '';
	public ServerRelativeUrl: string = '';
	public NativeMetadataUrl: string = '';
	original: IAIPropertyDefinition = {} as IAIPropertyDefinition;

	//-------------
	
	tokens: IEntityToken[] = [];

	private thisItem() : IAIPropertyDefinition {
		return {
			Id: this.Id,
			FieldName: this.FieldName,
			Prompt: this.Prompt,
			WorkspacePath: this.WorkspacePath,
			Expression: this.Expression,
			DisplayTitle: this.DisplayTitle,
			ServerRelativeUrl: this.ServerRelativeUrl,
			NativeMetadataUrl: this.NativeMetadataUrl,
		};
	}

	public dialogButtons: IReusableDialogFooterProps = {
		leftButtons: [/*
			{
				text: 'Delete',
				iconProps: {iconName: 'Delete'},
				onClick: (ev) => {
					this.dialog.onDismiss();
					this.onDelete(this.original);
				},
			}
		*/],
		rightButtons: [
			{
				text: 'Cancel',
				onClick: (ev) => {
					this.dialog.onDismiss();
				},
			},
			{
				text: 'Save',
				onClick: (ev) => {
					this.dialog.onDismiss();
					this.onSave(this.original, this.thisItem());
				},
			}
		],
	};

	onSave: (original:IAIPropertyDefinition, updated:IAIPropertyDefinition) => void;
	onDelete: (profile:IAIPropertyDefinition) => void;

	constructor(
		userConfig: IUserConfigPackage,
		onDelete: (profile:IAIPropertyDefinition) => void,
		onSave: (original:IAIPropertyDefinition, updated:IAIPropertyDefinition) => void
	) {
		this.userConfig = userConfig;
		this.onDelete = onDelete;
		this.onSave = onSave;
		
		this.dialog = new ReusableDialogState("AI Property Definition", 500);
		
		this.expressionState = new ExpressionFieldState(ExpressionBuilderOutputType.Boolean, e => {
			this.Expression = e;
		});

		makeAutoObservable(this);
	}

	public columns:IEntityColumn[] = [];
	public setColumns(c:IEntityColumn[]) {
		this.columns = c;
	}

	public setItem(item:IAIPropertyDefinition) {
		this.original = item;
		this.expressionState.setExpression(item.Expression);
		Object.assign(this, item);
	}
	
	public setFieldName(val:string) {
		this.FieldName = val;
	}

	public setPrompt(val:string) {
		this.Prompt = val;
	}

	public setWorkspacePath(val:string) {
		this.WorkspacePath = val;
	}

	public async ensureInitialLoaded() : Promise<void> {
		
	}
}

function AIPropertyEditor({
	state
}: {
	state: AIPropertyEditorState
}) {

	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);
		
	const dropdownColumns = state.columns.map((c) => {
			return {
				key: c.id,
				text: `${c.displayName} (${c.id})`,
			} as IDropdownOption;
		}).sort((a,b) => (a.text || '').toLowerCase().localeCompare((b.text || '').toLowerCase()));

	return (

		<ReusableDialog state={state.dialog} footerButtons={state.dialogButtons}>
			
			<MessageBar>Configure a property to be automatically populated by AI.</MessageBar>

			<InfoTooltipLabel 
				label='Field'
				title={`Select the field to be populated automatically.`}>
				<Dropdown
						options={dropdownColumns}
						selectedKey={state.FieldName}
						onChange={(e, option) => {
							state.setFieldName(option?.key as string);
						}}
					/>
			</InfoTooltipLabel>

			<InfoTooltipLabel 
				label='Prompt'
				title={`Give context to the AI about how to populate this value.`}>
				<TextField
					defaultValue={state.Prompt}
					onChange={(_, v) => state.setPrompt(v || '')}
				/>
			</InfoTooltipLabel>

			<InfoTooltipLabel label='Condition' title='Enter an expression to determine when the AI should attempt to populate this field.'>
				<ExpressionField state={state.expressionState} />
			</InfoTooltipLabel>

			<InfoTooltipLabel label='Document path pattern' title={`Optionally, specify document(s) which will be read by the AI to complete the field. This can be a filename, or a path. For example "*.docx" will match all word documents. "Correspondence/*" will match all documents in the correspondence folder. If you leave it blank, all documents are used.`}>
				<TextField
					value={state.WorkspacePath}
					onChange={(e, newValue) => { state.setWorkspacePath(newValue || ''); }}
				/>
			</InfoTooltipLabel>

		</ReusableDialog>
	);
}

export default observer(AIPropertyEditor);
